import React, { useState, useEffect } from "react";

import { mcpConfigService, MCPServerConfig } from "mcp/services/MCPConfigService";
import { useMCP } from "mcp/hooks/useMCP";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";

export const MCPConnectionPanel: React.FC = () => {
    const {
        clearError,
        connect,
        disconnect,
        error,
        forceReconnect,
        isCindraConnected,
        isCindraEnabled,
        isConnecting,
        lastConnectionAttempt,
        reconnectAttempts
    } = useMCP();

    const {
        authenticatedUserEmail,
        getAuthStatus,
        hasAuthToken,
        isCindraAuthenticated,
        tokenValidated,
        validatedUser
    } = useMCPAuth();

    const [savedConfigs, setSavedConfigs] = useState<MCPServerConfig[]>([]);
    const [selectedConfigName, setSelectedConfigName] = useState<string>("");
    const [customConfig, setCustomConfig] = useState<MCPServerConfig>({
        name: "",
        url: ""
    });
    const [envString, setEnvString] = useState("{}");
    const [useCustomConfig, setUseCustomConfig] = useState(false);
    const [authStatus, setAuthStatus] = useState<{ authenticated: boolean; email?: string } | null>(null);
    const [manualCheckLoading, setManualCheckLoading] = useState(false);

    useEffect(() => {
        if (isCindraEnabled) {
            const configs = mcpConfigService.getServerConfigs();
            setSavedConfigs(configs);
        }
    }, [isCindraEnabled]);

    // Only load auth status once when the panel first opens and when connection state changes
    useEffect(() => {
        if (!isCindraEnabled) {
            setAuthStatus(null);
            return;
        }

        // If we already have validated token data, use it instead of calling getAuthStatus
        if (tokenValidated === true && validatedUser) {
            setAuthStatus({
                authenticated: true,
                email: validatedUser.email
            });
        }
        else if (tokenValidated === false) {
            setAuthStatus({ authenticated: false });
        }
        else if (isCindraConnected && tokenValidated === null) {
            // Only call getAuthStatus if we're connected but don't have validation results yet
            const loadAuthStatus = async () => {
                try {
                    const status = await getAuthStatus();
                    setAuthStatus(status);
                }
                catch (error) {
                    console.error("[MCPConnectionPanel] Error getting auth status:", error);
                    setAuthStatus(null);
                }
            };

            void loadAuthStatus();
        }
        else if (!isCindraConnected) {
            setAuthStatus(null);
        }
    }, [isCindraEnabled, isCindraConnected, tokenValidated, validatedUser, getAuthStatus]);

    // If Cindra is disabled, show disabled state
    if (!isCindraEnabled) {
        return (
            <div className="bg-gray-50 border border-gray-300 rounded-lg shadow p-6">
                <div className="text-center space-y-4">
                    <div className="flex items-center justify-center space-x-2">
                        <div className="h-4 w-4 bg-gray-400 rounded-full"></div>
                        <h3 className="text-lg font-semibold text-gray-700">Cindra AI Features Disabled</h3>
                    </div>
                    <p className="text-gray-600 max-w-lg mx-auto">
                        Cindra AI features are currently disabled in this environment.
                        Contact your administrator to enable these features by setting the VITE_CINDRA_ENABLED environment variable to true.
                    </p>
                    <div className="bg-gray-100 border border-gray-200 rounded-md p-4 text-left">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">What are Cindra AI Features?</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• AI-powered chat assistant</li>
                            <li>• Healthcare data analysis</li>
                            <li>• Automated reporting and insights</li>
                            <li>• Integration with clinical systems via MCP</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    }

    const isValidUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        }
        catch {
            return false;
        }
    };

    const handleConnectSaved = async () => {
        if (!selectedConfigName) return;

        const config = mcpConfigService.getServerConfigByName(selectedConfigName);
        if (!config) return;

        try {
            await connect(config);
        }
        catch (error) {
            console.error("Connection failed:", error);
        }
    };

    const handleConnectCustom = async () => {
        if (!customConfig.url.trim() || !isValidUrl(customConfig.url)) return;

        const serverConfig: MCPServerConfig = {
            ...customConfig,
            env: envString.trim() ? JSON.parse(envString) : {}
        };

        try {
            await connect(serverConfig);
        }
        catch (error) {
            console.error("Connection failed:", error);
        }
    };

    const handleSaveCustomConfig = () => {
        if (!customConfig.name.trim() || !customConfig.url.trim() || !isValidUrl(customConfig.url)) return;

        const configToSave: MCPServerConfig = {
            ...customConfig,
            env: envString.trim() ? JSON.parse(envString) : {},
            description: "Custom configuration"
        };

        mcpConfigService.addServerConfig(configToSave);
        setSavedConfigs(mcpConfigService.getServerConfigs());

        // Reset form
        setCustomConfig({ name: "", url: "" });
        setEnvString("{}");
        setUseCustomConfig(false);
    };

    const handleDisconnect = async () => {
        try {
            await disconnect();
        }
        catch (error) {
            console.error("Disconnect failed:", error);
        }
    };

    const handleForceReconnect = async () => {
        try {
            await forceReconnect();
        }
        catch (error) {
            console.error("Force reconnect failed:", error);
        }
    };

    const handleManualAuthCheck = async () => {
        if (manualCheckLoading) return;

        setManualCheckLoading(true);
        try {
            const status = await getAuthStatus();
            setAuthStatus(status);
        }
        catch (error) {
            console.error("[MCPConnectionPanel] Manual auth check failed:", error);
            setAuthStatus(null);
        }
        finally {
            setManualCheckLoading(false);
        }
    };

    const formatLastAttempt = (date?: Date) => {
        if (!date) return null;
        return date.toLocaleTimeString();
    };

    return (
        <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
            <div className="space-y-6">
                {/* Cindra Status */}
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">Cindra AI Enabled</h3>
                            <div className="mt-2 text-sm text-green-700">
                                Cindra AI features are available in this environment.
                            </div>
                        </div>
                    </div>
                </div>
                {/* Connection Status */}
                {
                    error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                    <h3 className="text-sm font-medium text-red-800">Connection Error</h3>
                                    <div className="mt-2 text-sm text-red-700">{error}</div>
                                    {
                                        reconnectAttempts > 0 && (
                                            <div className="mt-2 text-sm text-red-600">
                                                Reconnection attempts: {reconnectAttempts}
                                                {
                                                    lastConnectionAttempt && (
                                                        <span className="ml-2">
                                                            (Last attempt: {formatLastAttempt(lastConnectionAttempt)})
                                                        </span>
                                                    )
                                                }
                                            </div>
                                        )
                                    }
                                    <div className="mt-3 flex space-x-2">
                                        <button
                                            onClick={clearError}
                                            className="text-sm bg-red-100 text-red-800 rounded px-2 py-1 hover:bg-red-200"
                                        >
                                            Dismiss
                                        </button>
                                        {
                                            !isConnecting && (
                                                <button
                                                    onClick={handleForceReconnect}
                                                    className="text-sm bg-blue-100 text-blue-800 rounded px-2 py-1 hover:bg-blue-200"
                                                >
                                                    Retry Connection
                                                </button>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
                {/* Reconnection Status */}
                {
                    !isCindraConnected && reconnectAttempts > 0 && !error && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="animate-spin h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">Reconnecting</h3>
                                    <div className="mt-2 text-sm text-yellow-700">
                                        Attempting to reconnect... (attempt #{reconnectAttempts})
                                        {
                                            lastConnectionAttempt && (
                                                <div className="mt-1">
                                                    Last attempt: {formatLastAttempt(lastConnectionAttempt)}
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                }
                {
                    isCindraConnected
                        ? (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                                        <span className="text-sm font-medium text-green-800">Connected to MCP Server</span>
                                    </div>
                                    {
                                        reconnectAttempts > 0 && (
                                            <span className="text-xs text-gray-500">
                                                Reconnected after {reconnectAttempts} attempts
                                            </span>
                                        )
                                    }
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={handleDisconnect}
                                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    >
                                        Disconnect
                                    </button>
                                    <button
                                        onClick={handleForceReconnect}
                                        disabled={isConnecting}
                                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                                    >
                                        Reconnect
                                    </button>
                                </div>
                            </div>
                        )
                        : (
                            <div className="space-y-6">
                                {/* Toggle between saved and custom configs */}
                                <div className="flex items-center space-x-4">
                                    <button
                                        onClick={() => setUseCustomConfig(false)}
                                        className={
                                            `px-3 py-2 text-sm font-medium rounded ${
                                                !useCustomConfig
                                                    ? "bg-blue-100 text-blue-700 border-blue-300"
                                                    : "bg-gray-100 text-gray-600 border-gray-300"
                                            } border`
                                        }
                                    >
                                        Saved Configurations
                                    </button>
                                    <button
                                        onClick={() => setUseCustomConfig(true)}
                                        className={
                                            `px-3 py-2 text-sm font-medium rounded ${
                                                useCustomConfig
                                                    ? "bg-blue-100 text-blue-700 border-blue-300"
                                                    : "bg-gray-100 text-gray-600 border-gray-300"
                                            } border`
                                        }
                                    >
                                        Custom Configuration
                                    </button>
                                </div>
                                {
                                    !useCustomConfig
                                        ? (
                                        /* Saved Configurations */
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="savedConfig" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Select Server Configuration
                                                    </label>
                                                    <select
                                                        id="savedConfig"
                                                        value={selectedConfigName}
                                                        onChange={(e) => setSelectedConfigName(e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value="">Choose a server configuration...</option>
                                                        {
                                                            savedConfigs.map((config) => (
                                                                <option key={config.name} value={config.name}>
                                                                    {config.name + " - " + (config.description || config.url)}
                                                                </option>
                                                            ))
                                                        }
                                                    </select>
                                                </div>
                                                {
                                                    selectedConfigName && (
                                                        <div className="bg-gray-50 p-3 rounded-md">
                                                            {
                                                                (() => {
                                                                    const config = mcpConfigService.getServerConfigByName(selectedConfigName);
                                                                    return config
                                                                        ? (
                                                                            <div className="text-sm text-gray-600">
                                                                                <p><strong>URL:</strong> {config.url}</p>
                                                                                {
                                                                                    config.env && Object.keys(config.env).length > 0 && (
                                                                                        <p><strong>Environment:</strong> {Object.keys(config.env).join(", ")}</p>
                                                                                    )
                                                                                }
                                                                                {
                                                                                    config.description && (
                                                                                        <p><strong>Description:</strong> {config.description}</p>
                                                                                    )
                                                                                }
                                                                            </div>
                                                                        )
                                                                        : null;
                                                                })()
                                                            }
                                                        </div>
                                                    )
                                                }
                                                <button
                                                    onClick={handleConnectSaved}
                                                    disabled={isConnecting || !selectedConfigName}
                                                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                >
                                                    {
                                                        isConnecting
                                                            ? (
                                                                <>
                                                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                    </svg>
                                                                    Connecting...
                                                                </>
                                                            )
                                                            : (
                                                                "Connect"
                                                            )
                                                    }
                                                </button>
                                            </div>
                                        )
                                        : (
                                            /* Custom Configuration */
                                            <div className="space-y-4">
                                                <div>
                                                    <label htmlFor="customServerName" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Server Name
                                                    </label>
                                                    <input
                                                        id="customServerName"
                                                        type="text"
                                                        value={customConfig.name}
                                                        onChange={(e) => setCustomConfig({ ...customConfig, name: e.target.value })}
                                                        placeholder="My Custom Server"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="customUrl" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Server URL *
                                                    </label>
                                                    <input
                                                        id="customUrl"
                                                        type="url"
                                                        value={customConfig.url}
                                                        onChange={(e) => setCustomConfig({ ...customConfig, url: e.target.value })}
                                                        placeholder="http://localhost:8001/ or https://api.example.com/mcp"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                                        required
                                                    />
                                                    {
                                                        customConfig.url && !isValidUrl(customConfig.url) && (
                                                            <p className="mt-1 text-sm text-red-600">Please enter a valid URL</p>
                                                        )
                                                    }
                                                </div>
                                                <div>
                                                    <label htmlFor="customEnv" className="block text-sm font-medium text-gray-700 mb-1">
                                                        Environment Variables (JSON)
                                                    </label>
                                                    <textarea
                                                        id="customEnv"
                                                        value={envString}
                                                        onChange={(e) => setEnvString(e.target.value)}
                                                        placeholder='{"API_KEY": "your-key", "DEBUG": "true"}'
                                                        rows={3}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                                                    />
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={handleConnectCustom}
                                                        disabled={isConnecting || !customConfig.url.trim() || !isValidUrl(customConfig.url)}
                                                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                                    >
                                                        {
                                                            isConnecting
                                                                ? (
                                                                    <>
                                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                        </svg>
                                                                        Connecting...
                                                                    </>
                                                                )
                                                                : (
                                                                    "Connect"
                                                                )
                                                        }
                                                    </button>
                                                    <button
                                                        onClick={handleSaveCustomConfig}
                                                        disabled={!customConfig.name.trim() || !customConfig.url.trim() || !isValidUrl(customConfig.url)}
                                                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Save Config
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                }
                            </div>
                        )
                }
                {/* Authentication Section */}
                <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold text-gray-900">
                            MCP Server Authentication
                        </h4>
                        {
                            isCindraConnected && (
                                <button
                                    onClick={handleManualAuthCheck}
                                    disabled={manualCheckLoading}
                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
                                >
                                    {manualCheckLoading ? "Checking..." : "Refresh"}
                                </button>
                            )
                        }
                    </div>
                    {
                        !isCindraConnected
                            ? (
                                <p className="text-sm text-gray-500">
                                    Connect to MCP server first to access authentication status.
                                </p>
                            )
                            : (
                                <>
                                    {
                                        (isCindraAuthenticated || authStatus?.authenticated || (tokenValidated === true && validatedUser))
                                            ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center space-x-2">
                                                        <div className="h-3 w-3 bg-green-400 rounded-full"></div>
                                                        <span className="text-sm text-green-800">
                                                            Authenticated{
                                                                authenticatedUserEmail
                                                                    ? ` as: ${authenticatedUserEmail}`
                                                                    : validatedUser?.email
                                                                        ? ` as: ${validatedUser.email}`
                                                                        : authStatus?.email
                                                                            ? ` as: ${authStatus.email}`
                                                                            : ""
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Method: App Token (Automatic)
                                                    </div>
                                                </div>
                                            )
                                            : tokenValidated === false
                                                ? (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="h-3 w-3 bg-red-400 rounded-full"></div>
                                                            <span className="text-sm text-red-600">
                                                                Authentication Failed
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Method: App Token (Automatic)
                                                        </div>
                                                        <div className="text-xs text-red-600 mt-2">
                                                            Token validation failed. Please refresh your session.
                                                        </div>
                                                    </div>
                                                )
                                                : (
                                                    <div className="space-y-2">
                                                        <div className="flex items-center space-x-2">
                                                            <div className="h-3 w-3 bg-gray-400 rounded-full"></div>
                                                            <span className="text-sm text-gray-600">
                                                                {hasAuthToken ? "Token Available (Checking...)" : "No Authentication Token"}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            Method: App Token (Automatic)
                                                        </div>
                                                        {
                                                            !hasAuthToken && (
                                                                <div className="text-xs text-red-600 mt-2">
                                                                    Please ensure you are logged into the main application.
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                )
                                    }
                                </>
                            )
                    }
                </div>
            </div>
        </div>
    );
};